import React, { useState, useEffect } from 'react'
import logoImg from "../../images/Logo/LogoHeader.png"
// import "./header.scss"
import { FaFacebookF, FaLinkedinIn, FaYoutube, FaPhoneAlt, FaSearch } from "react-icons/fa";
import { BsTwitter } from "react-icons/bs";
import { useNavigate, useLocation } from "react-router-dom";
import { AiOutlineMenu } from 'react-icons/ai'
import { FaArrowAltCircleRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { getActiveLanguages } from '../../apis/languages';
import { renderFlag } from '../../utils/renderFlag';
import { normalizeLang } from '../../utils/localeDataLoader';
import { getNavMenuByLanguage } from '../../apis/navMenus';
import { getEcosystemGroupsByLanguage } from '../../apis/adminEcosystemGroups';
import { getNewsCategoriesByLanguage } from '../../apis/newsCategories';
import { getRecruitmentDepartmentsByLanguage } from '../../apis/recruitments';
import { cacheNavMenuRaw } from '../../utils/navMenuCache';

function getCorrectSlug(slug, cleanBase, itemId, isEn) {
    const raw = (slug || '').trim().replace(/^\//, '');
    if (!raw) return '';

    const ecoPrefix = isEn ? '/ecosystem' : '/he-sinh-thai';

    // Check if slug is an ecosystem item
    const isEco =
        cleanBase === 'he-sinh-thai-hsv' ||
        cleanBase === 'he-sinh-thai' ||
        cleanBase === 'ecosystem' ||
        itemId === 43 ||
        /hsv|phong-kham|benh-vien|dao-tao|giao-duc|truyen-thong|thuong-mai|healthcare|clinic|education|technology|pharma|investment|infrastructure/.test(raw) ||
        /\bcorp\b/.test(raw.replace(/-/g, ' '));

    if (isEco) {
        const subClean = raw.replace(/^(he-sinh-thai|ecosystem|san-pham-giai-phap|products-solutions)\//, '').replace(/_/g, '-');
        return `${ecoPrefix}/${subClean}`;
    }

    if (raw.includes('/')) {
        return `/${raw}`;
    }

    return `/${cleanBase}/${raw}`;
}

function transformNavMenuData(apiData, languageCode = 'vi') {
    const isEn = languageCode === 'en';

    return apiData.map(item => {
        const basePath = item.slug;
        const cleanBase = (basePath || '').replace(/^\//, '');

        if (item.children && item.children.length > 0) {
            return {
                id: item.id,
                keyName: item.keyName,
                text: item.title,
                slug: basePath,
                sortOrder: item.sortOrder ?? item.SortOrder ?? 0,
                level: item.level ?? item.Level ?? 1,
                sub: item.children.map(child => {
                    let lv2Slug = getCorrectSlug(child.slug, cleanBase, item.id, isEn);

                    if (child.keyName === 'about_menu.overview') {
                        lv2Slug = `/${cleanBase}`;
                    } else if (child.keyName === 'about_menu.leadership') {
                        lv2Slug = isEn ? `/${cleanBase}/leadership/1` : `/${cleanBase}/ban-lanh-dao/1`;
                    }

                    // Map Level 3 children if present
                    let children_lv3 = [];
                    if (child.children && child.children.length > 0) {
                        children_lv3 = child.children.map(subChild => {
                            let lv3Slug = getCorrectSlug(subChild.slug, cleanBase, item.id, isEn);
                            return {
                                id_lv3: subChild.id,
                                keyName_lv3: subChild.keyName,
                                textItem_lv3: subChild.title,
                                slug_lv3: lv3Slug,
                            };
                        });
                    }

                    return {
                        id_lv2: child.id,
                        keyName_lv2: child.keyName,
                        textItem_lv2: child.title,
                        slug_lv2: lv2Slug,
                        children_lv3: children_lv3,
                    };
                }),
            };
        }

        return {
            id: item.id,
            keyName: item.keyName,
            text: item.title,
            slug: basePath,
            sortOrder: item.sortOrder ?? item.SortOrder ?? 0,
            level: item.level ?? item.Level ?? 1,
        };
    });
}

function appendCorporateMenuItems(items, languageCode, ecosystemGroups = []) {
    const isEnglish = languageCode === 'en';

    // Standard fallback sub-items cho Tuyển dụng
    const fallbackRecruitmentChildren = isEnglish
        ? [
            { id_lv2: 'recruitment-culture', keyName_lv2: 'recruitment.culture', textItem_lv2: 'Culture & work environment', slug_lv2: '/recruitment/culture-work-environment' },
            { id_lv2: 'recruitment-policies', keyName_lv2: 'recruitment.policies', textItem_lv2: 'HR policies', slug_lv2: '/recruitment/hr-policies' },
            { id_lv2: 'recruitment-jobs', keyName_lv2: 'recruitment.jobs', textItem_lv2: 'Career opportunities', slug_lv2: '/recruitment/career-opportunities' },
        ]
        : [
            { id_lv2: 'recruitment-culture', keyName_lv2: 'recruitment.culture', textItem_lv2: 'Văn hóa và môi trường làm việc', slug_lv2: '/tuyen-dung/van-hoa-va-moi-truong-lam-viec' },
            { id_lv2: 'recruitment-policies', keyName_lv2: 'recruitment.policies', textItem_lv2: 'Chính sách nhân sự', slug_lv2: '/tuyen-dung/chinh-sach-nhan-su' },
            { id_lv2: 'recruitment-jobs', keyName_lv2: 'recruitment.jobs', textItem_lv2: 'Công việc tuyển dụng', slug_lv2: '/tuyen-dung/cong-viec-tuyen-dung' },
        ];

    // Fallback chuẩn cho Hệ sinh thái khi API trống hoặc lỗi
    const fallbackEcosystemChildren = isEnglish
        ? [
            { id_lv2: 'eco-hospitals', keyName_lv2: 'ecosystem.hospital', textItem_lv2: 'Hospitals', slug_lv2: '/ecosystem/hospital' },
            { id_lv2: 'eco-clinics', keyName_lv2: 'ecosystem.clinic', textItem_lv2: 'Clinics', slug_lv2: '/ecosystem/clinic' },
            { id_lv2: 'eco-pharma', keyName_lv2: 'ecosystem.pharmaceutical-medical-equipment', textItem_lv2: 'Pharma & MedTech', slug_lv2: '/ecosystem/pharmaceutical-medical-equipment' },
            { id_lv2: 'eco-tech', keyName_lv2: 'ecosystem.technology', textItem_lv2: 'Tech & Digital Transformation', slug_lv2: '/ecosystem/technology' },
            { id_lv2: 'eco-education', keyName_lv2: 'ecosystem.education', textItem_lv2: 'Education & Training', slug_lv2: '/ecosystem/education' },
            { id_lv2: 'eco-investment', keyName_lv2: 'ecosystem.investment-services', textItem_lv2: 'Investment & Services', slug_lv2: '/ecosystem/investment-services' },
        ]
        : [
            { id_lv2: 'eco-hospitals', keyName_lv2: 'ecosystem.hospital', textItem_lv2: 'Chuỗi bệnh viện', slug_lv2: '/he-sinh-thai/chuoi-benh-vien' },
            { id_lv2: 'eco-clinics', keyName_lv2: 'ecosystem.clinic', textItem_lv2: 'Chuỗi phòng khám', slug_lv2: '/he-sinh-thai/chuoi-phong-kham' },
            { id_lv2: 'eco-pharma', keyName_lv2: 'ecosystem.pharmaceutical-medical-equipment', textItem_lv2: 'Dược phẩm & Thiết bị y tế', slug_lv2: '/he-sinh-thai/duoc-pham-va-thiet-bi-y-te' },
            { id_lv2: 'eco-tech', keyName_lv2: 'ecosystem.technology', textItem_lv2: 'Công nghệ & Chuyển đổi số', slug_lv2: '/he-sinh-thai/cong-nghe-va-chuyen-doi-so' },
            { id_lv2: 'eco-education', keyName_lv2: 'ecosystem.education', textItem_lv2: 'Đào tạo & Giáo dục', slug_lv2: '/he-sinh-thai/giao-duc-va-dao-tao' },
            { id_lv2: 'eco-investment', keyName_lv2: 'ecosystem.investment-services', textItem_lv2: 'Đầu tư & Dịch vụ', slug_lv2: '/he-sinh-thai/dau-tu-va-dich-vu' },
        ];

    const ecosystemSlugs = new Set(
        (Array.isArray(ecosystemGroups) ? ecosystemGroups : []).flatMap(g => [g.slug, g.code, g.slug?.toLowerCase(), g.code?.toLowerCase()]).filter(Boolean)
    );

    const hiddenTopLevelKeys = new Set([
        'menu.investment',
        'menu.projects'
    ]);

    const hiddenTopLevelSlugs = new Set([
        'chuoi-benh-vien', 'hospital', 'chuoi-benh-vien-1',
        'chuoi-phong-kham', 'clinic', 'chuoi-phong-kham-1',
        'giao-duc-dao-tao', 'giao-duc-va-dao-tao', 'education',
        'cong-nghe-chuyen-doi-so', 'cong-nghe-va-chuyen-doi-so', 'technology',
        'dau-tu-dich-vu', 'dau-tu-va-dich-vu', 'investment-services',
        'duoc-pham-thiet-bi-y-te', 'duoc-pham-va-thiet-bi-y-te', 'pharmaceutical-medical-equipment',
        'dau-tu', 'investment',
        'du-an', 'projects',
        'van-hoa-va-moi-truong-lam-viec', 'culture-work-environment',
        'chinh-sach-nhan-su', 'hr-policies',
        'cong-viec-tuyen-dung', 'career-opportunities',
        'hoi-dong-co-dong', 'shareholder-council',
        'tai-chinh', 'finance',
        'quan-tri-cong-ty', 'corporate-governance',
        'bao-cao-thuong-nien', 'annual-report',
        'bao-cao-quan-tri', 'governance-report',
        'khach-hang-tieu-bieu', 'typical-customers',
        'doi-tac-chien-luoc', 'strategic-partners',
        'benh-vien', 'hospitals',
        'phong-kham-nha-khoa', 'clinics-dental',
        'bao-hiem', 'insurance',
        'cac-nganh-dich-vu', 'service-industries',
        'ngan-hang', 'banking-finance',
        'chuoi-cung-ung-thiet-bi-y-te', 'medical-supply-chain', 'medical-supply'
    ]);

    const visibleItems = items.filter(item => {
        const key = (item.keyName || '').toLowerCase();
        const slug = (item.slug || '').toLowerCase().replace(/^\//, '');
        if (hiddenTopLevelKeys.has(key)) return false;
        if (hiddenTopLevelSlugs.has(slug)) return false;
        if (ecosystemSlugs.has(slug)) return false;
        if (key.startsWith('ecosystem.') && key !== 'menu.ecosystem') return false;
        if (key.startsWith('recruitment.') && key !== 'menu.recruitment') return false;
        if (key.startsWith('shareholder.') && key !== 'menu.shareholders') return false;
        if (key.startsWith('about_menu.') && key !== 'menu.about' && key !== 'about_menu.ecosystem' && key !== 'about_menu.partners') return false;
        if (key.startsWith('customers_menu.')) return false;
        return true;
    });

    const ecosystemMenuIndex = visibleItems.findIndex(item =>
        item.keyName === 'menu.ecosystem' || item.keyName === 'about_menu.ecosystem' || ['he-sinh-thai', 'he-sinh-thai-hsv', 'ecosystem'].includes(item.slug)
    );

    const existingEcosystemChildren = ecosystemMenuIndex >= 0 && visibleItems[ecosystemMenuIndex]?.sub && visibleItems[ecosystemMenuIndex].sub.length > 0
        ? visibleItems[ecosystemMenuIndex].sub
        : [];

    const ecosystemChildren = existingEcosystemChildren.length > 0
        ? existingEcosystemChildren
        : (ecosystemGroups.length > 0
            ? ecosystemGroups.slice(0, 7).map((group) => ({
                id_lv2: `ecosystem-${group.id}`,
                keyName_lv2: `ecosystem.${group.code || group.id}`,
                textItem_lv2: group.name || group.title,
                slug_lv2: group.websiteUrl || `${isEnglish ? '/ecosystem' : '/he-sinh-thai'}/${group.slug || group.code}`,
            }))
            : fallbackEcosystemChildren
        );

    const normalizedItems = [...visibleItems];

    const ecosystemMenu = {
        ...(ecosystemMenuIndex >= 0 ? normalizedItems[ecosystemMenuIndex] : {}),
        id: ecosystemMenuIndex >= 0 ? normalizedItems[ecosystemMenuIndex].id : 'public-ecosystem',
        keyName: 'menu.ecosystem',
        text: (ecosystemMenuIndex >= 0 && normalizedItems[ecosystemMenuIndex].text) || (isEnglish ? 'Ecosystem' : 'Hệ sinh thái'),
        slug: isEnglish ? 'ecosystem' : 'he-sinh-thai-hsv',
        level: (ecosystemMenuIndex >= 0 && normalizedItems[ecosystemMenuIndex].level) || 1,
        sub: ecosystemChildren,
    };
    if (ecosystemMenuIndex >= 0) normalizedItems[ecosystemMenuIndex] = ecosystemMenu;
    else normalizedItems.push(ecosystemMenu);

    const hasShareholders = visibleItems.some(item =>
        item.keyName === 'menu.shareholders' || ['co-dong', 'shareholders'].includes(item.slug)
    );
    const hasNews = visibleItems.some(item =>
        item.keyName === 'menu.news' || ['tin-tuc', 'news'].includes(item.slug)
    );

    const recruitmentMenuIndex = normalizedItems.findIndex(item =>
        item.keyName === 'menu.recruitment' || ['tuyen-dung', 'recruitment'].includes(item.slug)
    );

    const existingRecruitmentChildren = recruitmentMenuIndex >= 0 && normalizedItems[recruitmentMenuIndex]?.sub && normalizedItems[recruitmentMenuIndex].sub.length > 0
        ? normalizedItems[recruitmentMenuIndex].sub
        : [];

    const finalRecruitmentChildren = existingRecruitmentChildren.length > 0
        ? existingRecruitmentChildren
        : fallbackRecruitmentChildren;

    const recruitmentMenu = {
        ...(recruitmentMenuIndex >= 0 ? normalizedItems[recruitmentMenuIndex] : {}),
        id: recruitmentMenuIndex >= 0 ? normalizedItems[recruitmentMenuIndex].id : 'public-recruitment',
        keyName: 'menu.recruitment',
        text: (recruitmentMenuIndex >= 0 && normalizedItems[recruitmentMenuIndex].text) || (isEnglish ? 'Careers' : 'Tuyển dụng'),
        slug: isEnglish ? 'recruitment' : 'tuyen-dung',
        level: (recruitmentMenuIndex >= 0 && normalizedItems[recruitmentMenuIndex].level) || 2,
        sub: finalRecruitmentChildren,
    };
    if (recruitmentMenuIndex >= 0) normalizedItems[recruitmentMenuIndex] = recruitmentMenu;
    else normalizedItems.push(recruitmentMenu);

    const hasRecruitment = normalizedItems.some(item =>
        item.keyName === 'menu.recruitment' || ['tuyen-dung', 'recruitment'].includes(item.slug)
    );

    const allItems = [
        ...normalizedItems,
        ...(!hasShareholders ? [{
            id: 'public-shareholders',
            keyName: 'menu.shareholders',
            text: isEnglish ? 'Shareholders' : 'Cổ đông',
            slug: isEnglish ? 'shareholders' : 'co-dong',
            level: 1,
            sub: [
                { id_lv2: 'shareholder-council', textItem_lv2: isEnglish ? 'Shareholder council' : 'Hội đồng cổ đông', slug_lv2: isEnglish ? '/shareholders/shareholder-council' : '/co-dong/hoi-dong-co-dong' },
                { id_lv2: 'shareholder-finance', textItem_lv2: isEnglish ? 'Finance' : 'Tài chính', slug_lv2: isEnglish ? '/shareholders/finance' : '/co-dong/tai-chinh' },
                { id_lv2: 'shareholder-governance', textItem_lv2: isEnglish ? 'Corporate governance' : 'Quản trị công ty', slug_lv2: isEnglish ? '/shareholders/corporate-governance' : '/co-dong/quan-tri-cong-ty' },
                { id_lv2: 'shareholder-annual', textItem_lv2: isEnglish ? 'Annual reports' : 'Báo cáo thường niên', slug_lv2: isEnglish ? '/shareholders/annual-report' : '/co-dong/bao-cao-thuong-nien' },
                { id_lv2: 'shareholder-governance-report', textItem_lv2: isEnglish ? 'Governance reports' : 'Báo cáo quản trị', slug_lv2: isEnglish ? '/shareholders/governance-report' : '/co-dong/bao-cao-quan-tri' },
            ],
        }] : []),
        ...(!hasNews ? [{
            id: 'public-news',
            keyName: 'menu.news',
            text: isEnglish ? 'News' : 'Tin tức',
            slug: isEnglish ? 'news' : 'tin-tuc',
            level: 2,
        }] : []),
        ...(!hasRecruitment ? [{
            id: 'public-recruitment',
            keyName: 'menu.recruitment',
            text: isEnglish ? 'Careers' : 'Tuyển dụng',
            slug: isEnglish ? 'recruitment' : 'tuyen-dung',
            level: 2,
            sub: fallbackRecruitmentChildren,
        }] : []),
    ];

    const getMenuOrder = (item) => {
        const key = (item.keyName || '').toLowerCase();
        const slug = (item.slug || '').toLowerCase();
        const text = (item.text || item.title || '').toLowerCase();

        if (key === 'menu.home' || slug === 'trang-chu' || slug === 'home' || slug === '') return 1;
        if (key === 'menu.about' || slug === 'tong-quan' || slug === 'about-us' || slug.includes('tong-quan')) return 2;
        if (key === 'menu.shareholders' || slug === 'co-dong' || slug === 'shareholders' || slug.includes('co-dong')) return 3;
        if (key === 'menu.products' || slug.includes('san-pham') || slug.includes('giai-phap') || text.includes('sản phẩm') || text.includes('giai phap')) return 4;
        if (key === 'menu.ecosystem' || key === 'about_menu.ecosystem' || slug === 'he-sinh-thai' || slug === 'he-sinh-thai-hsv' || slug === 'ecosystem') return 5;
        if (key === 'hsv_corp' || key === 'hsvcorp' || slug === 'hsvcorp' || slug === 'hsv-corp') return 6;
        if (key === 'hsv_thuong_mai' || key === 'hsv_thuongmai' || slug === 'hsv-thuong-mai' || slug === 'hsv-commercial') return 7;
        if (slug.includes('khach-hang') || slug.includes('doi-tac') || slug.includes('partners') || text.includes('khách hàng') || text.includes('đối tác') || key === 'about_menu.partners') return 8;
        if (key === 'menu.recruitment' || slug === 'tuyen-dung' || slug === 'recruitment' || slug.includes('tuyen-dung')) return 9;
        if (key === 'menu.news' || slug === 'tin-tuc' || slug === 'news' || slug.includes('tin-tuc')) return 10;
        if (key === 'menu.contact' || slug === 'lien-he' || slug === 'contact' || slug.includes('lien-he')) return 11;

        const explicitOrder = Number(item.sortOrder ?? item.SortOrder ?? 0);
        if (explicitOrder > 0) return 15 + explicitOrder;

        return 50;
    };

    const uniqueItemsMap = new Map();
    allItems.forEach(item => {
        const uniqueKey = item.keyName || item.slug || item.id;
        if (!uniqueItemsMap.has(uniqueKey)) {
            uniqueItemsMap.set(uniqueKey, item);
        }
    });

    return Array.from(uniqueItemsMap.values()).sort((a, b) => getMenuOrder(a) - getMenuOrder(b));
}

function Header({ scrollValue }) {
    const { t, i18n } = useTranslation();
    const [activeMenuId, setActiveMenuId] = useState(null); // Không hardcode ID = 1 nữa
    const [activeSubId, setActiveSubId] = useState("");
    const [activeTopMenuId, setActiveTopMenuId] = useState(null); // For recruitment and news buttons
    const navigate = useNavigate();
    const location = useLocation();
    const [activeMenuMobile, setActiveMenuMobile] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [listMenu, setListMenu] = useState([]);
    const [languages, setLanguages] = useState([]);

    useEffect(() => {
        const loadLanguages = async () => {
            try {
                const response = await getActiveLanguages();
                let listLang = [];
                if (response.success && response.data && Array.isArray(response.data)) {
                    listLang = response.data;
                } else if (Array.isArray(response)) {
                    listLang = response;
                } else if (response.data && Array.isArray(response.data)) {
                    listLang = response.data;
                }

                if (listLang && listLang.length > 0) {
                    setLanguages(listLang);
                }
            } catch (error) {
                console.error("Lỗi lấy danh sách ngôn ngữ:", error);
            }
        };
        loadLanguages();
    }, []);

    useEffect(() => {
        const fetchMenu = async () => {
            try {
                const lang = normalizeLang(i18n.language);
                const [data, ecosystemResponse] = await Promise.all([
                    getNavMenuByLanguage(lang),
                    getEcosystemGroupsByLanguage(lang).catch(() => null),
                ]);
                cacheNavMenuRaw(lang, data);
                const ecosystemGroups = ecosystemResponse?.data?.items
                    || ecosystemResponse?.items
                    || ecosystemResponse?.data
                    || [];
                setListMenu(appendCorporateMenuItems(
                    transformNavMenuData(data, lang),
                    lang,
                    Array.isArray(ecosystemGroups) ? ecosystemGroups : [],
                ));
            } catch (error) {
                console.error('Lỗi khi tải menu từ API:', error);
            }
        };
        fetchMenu();
    }, [i18n.language]);

    const nextLang = languages.length > 1
        ? languages[(languages.findIndex(l => l.code === normalizeLang(i18n.language)) + 1) % languages.length]
        : null;

    const handleLangChange = (lang) => {
        i18n.changeLanguage(lang.code);
        localStorage.setItem('selectedLanguageCode', lang.code);
    };

    useEffect(() => {
        const currentPath = location.pathname;

        // Check for top menu buttons (recruitment, news)
        if (currentPath === '/tuyen-dung' || currentPath === '/recruitment' || currentPath.startsWith('/tuyen-dung/') || currentPath.startsWith('/recruitment/')) {
            setActiveTopMenuId('recruitment');
            setActiveMenuId(null);
            setActiveSubId("");
            return;
        }
        if (currentPath === '/tin-tuc' || currentPath === '/news' || currentPath.startsWith('/tin-tuc/') || currentPath.startsWith('/news/')) {
            setActiveTopMenuId('news');
            setActiveMenuId(null);
            setActiveSubId("");
            return;
        }

        setActiveTopMenuId(null);

        // Chờ listMenu load xong mới xử lý active menu
        // Tránh trường hợp menu chưa fetch về từ API, dẫn đến highlight sai về Home
        if (listMenu.length === 0) return;

        let found = false;

        const normCurrentPath = currentPath.endsWith('/') && currentPath.length > 1 ? currentPath.slice(0, -1) : currentPath;

        // Đặc biệt xử lý route Khách hàng và đối tác (vì nó nằm trong layout Tổng quan nhưng lại là menu cấp 1)
        if (normCurrentPath.includes('/khach-hang-doi-tac') || normCurrentPath.includes('/customers-partners')) {
            const customerMenu = listMenu.find(item => item.keyName === 'about_menu.partners' || item.slug === 'khach-hang-doi-tac' || item.slug === 'customers-partners');
            if (customerMenu) {
                setActiveMenuId(customerMenu.id);
                setActiveSubId("");
                return;
            }
        }

        // Kiểm tra exact match trước
        for (const item of listMenu) {
            // Kiểm tra sub-menu trước vì có trường hợp sub-menu trùng path với parent (ví dụ: Tổng quan /gioi-thieu)
            if (item.sub) {
                for (const subItem of item.sub) {
                    const normSubSlug = subItem.slug_lv2.endsWith('/') && subItem.slug_lv2.length > 1 ? subItem.slug_lv2.slice(0, -1) : subItem.slug_lv2;
                    if (normCurrentPath === normSubSlug) {
                        console.log("Matched submenu exact:", subItem.slug_lv2);
                        setActiveMenuId(item.id);
                        setActiveSubId(subItem.id_lv2);
                        found = true;
                        break;
                    }
                }
            }
            if (found) break;

            const normItemSlug = `/${item.slug}`.endsWith('/') && `/${item.slug}`.length > 1 ? `/${item.slug}`.slice(0, -1) : `/${item.slug}`;
            if (normCurrentPath === normItemSlug) {
                console.log("Matched parent exact:", item.slug);
                setActiveMenuId(item.id);
                setActiveSubId("");
                found = true;
                break;
            }
        }

        // Nếu không match, kiểm tra path bắt đầu với submenu slug (cho multi-level routes)
        // Ví dụ: /gioi-thieu/ban-lanh-dao/{slug-leaderCategory} sẽ match /gioi-thieu/ban-lanh-dao
        if (!found) {
            for (const item of listMenu) {
                if (item.sub) {
                    for (const subItem of item.sub) {
                        const normSubSlug = subItem.slug_lv2.endsWith('/') && subItem.slug_lv2.length > 1 ? subItem.slug_lv2.slice(0, -1) : subItem.slug_lv2;
                        // Lấy base slug không bao gồm segment số cuối (ví dụ /gioi-thieu/ban-lanh-dao/1 -> /gioi-thieu/ban-lanh-dao)
                        const slugParts = normSubSlug.split('/');
                        // Nếu segment cuối là số thì bỏ đi để lấy base path
                        const lastPart = slugParts[slugParts.length - 1];
                        const basePath = /^\d+$/.test(lastPart)
                            ? slugParts.slice(0, -1).join('/')
                            : normSubSlug;

                        const slugSegments = basePath.split('/').filter(Boolean).length;
                        if (
                            normCurrentPath === normSubSlug ||
                            (slugSegments > 1 && normCurrentPath.startsWith(basePath + '/')) ||
                            (slugSegments > 1 && normCurrentPath.startsWith(normSubSlug + '/'))
                        ) {
                            setActiveMenuId(item.id);
                            setActiveSubId(subItem.id_lv2);
                            found = true;
                            break;
                        }
                    }
                }
                if (found) break;
            }
        }

        // Fallback: nếu URL là trang con thuộc một mục cha (vd /gioi-thieu/tam-nhin) nhưng không nằm trong submenu
        if (!found) {
            for (const item of listMenu) {
                const normItemSlug = `/${item.slug}`.endsWith('/') && `/${item.slug}`.length > 1 ? `/${item.slug}`.slice(0, -1) : `/${item.slug}`;
                if (normCurrentPath.startsWith(normItemSlug + '/')) {
                    setActiveMenuId(item.id);
                    setActiveSubId("");
                    found = true;
                    break;
                }
            }
        }

        // Đặc biệt xử lý route chi tiết Ban lãnh đạo
        if (!found && (currentPath.includes('/ban-lanh-dao/') || currentPath.includes('/leadership/'))) {
            const aboutMenu = listMenu.find(item => item.keyName === 'menu.about');
            if (aboutMenu) {
                setActiveMenuId(aboutMenu.id);
                const leadershipSub = aboutMenu.sub?.find(sub => sub.keyName_lv2 === 'about_menu.leadership');
                if (leadershipSub) {
                    setActiveSubId(leadershipSub.id_lv2);
                }
            }
        }
    }, [location.pathname, listMenu]);

    useEffect(() => {
        if (activeTopMenuId === 'recruitment') {
            document.title = `HSV - ${t('menu.recruitment')}`;
        } else if (activeTopMenuId === 'news') {
            document.title = `HSV - ${t('menu.news')}`;
        } else {
            const activeItem = listMenu.find(item => item.id === activeMenuId);
            document.title = `HSV - ${activeItem ? activeItem.text : t('common.home')}`;
        }
    }, [activeMenuId, listMenu, activeTopMenuId, t, i18n.language]);

    const handleActiveMenu = (menuId, slug) => {
        setActiveMenuId(menuId)
        if (slug !== undefined && slug !== null) {
            const targetPath = slug.startsWith('/') ? slug : `/${slug}`
            navigate(targetPath)
            setActiveMenuMobile(false)
        }
    }

    const handleActiveMenuSub = (menuId, subId, slug) => {
        setActiveMenuId(menuId)
        setActiveSubId(subId)
        if (slug !== undefined && slug !== null) {
            const targetPath = slug.startsWith('/') ? slug : `/${slug}`
            navigate(targetPath)
            setActiveMenuMobile(false)
        }
    }
    const handleLogoOnclick = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        // Lấy slug trang chủ thông qua keyName
        const homeItem = listMenu.find(item => item.keyName === 'menu.home') || listMenu[0];
        if (homeItem && homeItem.slug) {
            navigate(`/${homeItem.slug}`);
        } else {
            // Fallback theo ngôn ngữ nếu menu chưa tải xong
            const lang = normalizeLang(i18n.language);
            navigate(lang === 'vi' ? '/trang-chu' : '/home');
        }
    }

    const handleActiveMobileOnclick = () => {
        setActiveMenuMobile(true);
    };

    const getItemLevel = (item) => {
        if (item.level !== undefined && item.level !== null) {
            const num = Number(item.level);
            if (!isNaN(num) && num > 0) return num;
        }
        const key = (item.keyName || '').toLowerCase();
        const slug = (item.slug || '').toLowerCase();
        if (
            key === 'menu.recruitment' || slug.includes('tuyen-dung') || slug.includes('recruitment') ||
            key === 'menu.news' || slug.includes('tin-tuc') || slug.includes('news') ||
            key === 'menu.contact' || slug.includes('lien-he') || slug.includes('contact')
        ) {
            return 2;
        }
        return 1;
    };

    const menusByLevel = {};
    listMenu.forEach(item => {
        const lvl = getItemLevel(item);
        if (!menusByLevel[lvl]) menusByLevel[lvl] = [];
        menusByLevel[lvl].push(item);
    });

    const level1Menus = menusByLevel[1] || [];
    const level2Menus = menusByLevel[2] || [];
    const extraLevels = Object.keys(menusByLevel)
        .map(Number)
        .filter(lvl => lvl > 2)
        .sort((a, b) => a - b);

    const renderMenuItem = (value, index) => (
        <li className='header__menu-item header__menu-item-lv1' key={value.id || value.keyName || index} onClick={() => { if (!value.sub || value.sub.length === 0) handleActiveMenu(value.id, value.slug); }}>
            <p className={activeMenuId === value.id ? "header__menu-active" : ""}>
                {value.text}
            </p>
            <span className="menu-line" style={{ width: `${activeMenuId === value.id ? "90%" : ""}` }}></span>
            {value.sub && value.sub.length > 0 && (
                <ul className="header__menu-list-c2">
                    {value.sub.map((valueSub, indexSub) => (
                        <React.Fragment key={valueSub.id_lv2 || indexSub}>
                            <li
                                className={`header__menu-subitem-lv2 ${valueSub.children_lv3 && valueSub.children_lv3.length > 0 ? 'has-c3' : 'no-c3'}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleActiveMenuSub(value.id, valueSub.id_lv2, valueSub.slug_lv2);
                                }}
                            >
                                <p className={activeMenuId === value.id && activeSubId === valueSub.id_lv2 ? "header__menu-active" : ""}>
                                    {valueSub.textItem_lv2}
                                </p>
                            </li>
                            {valueSub.children_lv3 && valueSub.children_lv3.map((sub3, idx3) => (
                                <li
                                    className="header__menu-subitem-lv3"
                                    key={sub3.id_lv3 || idx3}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleActiveMenuSub(value.id, sub3.id_lv3, sub3.slug_lv3);
                                    }}
                                >
                                    <p className={activeMenuId === value.id && activeSubId === sub3.id_lv3 ? "header__menu-active" : ""}>
                                        {sub3.textItem_lv3}
                                    </p>
                                </li>
                            ))}
                        </React.Fragment>
                    ))}
                </ul>
            )}
        </li>
    );

    return (
        <div className={scrollValue > 100 ? "header header-active" : "header"}>
            <div className="header-light-beams" aria-hidden="true" />
            <div className="layout_container">
                <div className="row">
                    <div className="header__menu">
                        <div className="col-xs-12 col-xl-3" style={{ display: 'flex', alignItems: 'center' }}>
                            <div className="header__logo">
                                <img src={logoImg} alt="logo" style={{ cursor: "pointer" }} onClick={() => handleLogoOnclick()}></img>
                            </div>
                        </div>
                        <div className='col-xs-12 col-xl-9' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <div className="header__menu-top">
                                {/* Primary Menu Bar (Row 1 / Level 1) */}
                                <ul className={activeMenuMobile ? "header__menu-list header__menu-text header__menu-text-active" : "header__menu-list header__menu-text"}>
                                    <li onClick={() => setActiveMenuMobile(false)} className='header__menu-item header__menu-item-rightIcon' aria-label={t('common.close') || 'Đóng menu'}><FaArrowAltCircleRight /></li>
                                    {activeMenuMobile
                                        ? listMenu.map((value, index) => renderMenuItem(value, index))
                                        : level1Menus.map((value, index) => renderMenuItem(value, index))}
                                </ul>

                                {/* Secondary Submenu Bar (Row 2 / Level 2) & Icons */}
                                <div className="header__menu-sub2-row">
                                    {!activeMenuMobile && (
                                        <a
                                            href="tel:0398911118"
                                            className="header__hotline-badge"
                                            title="Hotline 0398911118"
                                        >
                                            <FaPhoneAlt className="hotline-icon" />
                                            <span className="hotline-number">0398911118</span>
                                        </a>
                                    )}

                                    {level2Menus.length > 0 && !activeMenuMobile && (
                                        <ul className="header__menu-list header__menu-text header__menu-text-sub2">
                                            {level2Menus.map((value, index) => renderMenuItem(value, index))}
                                        </ul>
                                    )}
                                    <ul className="header__menu-listtop header__menu-icon">
                                        <li className='header__menu-item header__menu-icon-mobile' onClick={() => handleActiveMobileOnclick()}><AiOutlineMenu /></li>
                                        {nextLang && (
                                            <li
                                                className='header__menu-item header__lang-item'
                                                onClick={() => handleLangChange(nextLang)}
                                                title={nextLang.name}
                                            >
                                                {renderFlag(nextLang, { size: '20px' })}
                                            </li>
                                        )}
                                        <li className='header__menu-item' onClick={() => window.open("https://www.facebook.com/profile.php?id=100063723542675", "_blank")} title="Facebook"><FaFacebookF /></li>
                                        <li className='header__menu-item' onClick={() => window.open("https://www.youtube.com/watch?v=kYRAM1ZohXM", "_blank")} title="YouTube"><FaYoutube /></li>
                                        <li className='header__menu-item header__menu-item--search' onClick={() => setIsSearchOpen(!isSearchOpen)} title={t('common.search') || 'Tìm kiếm'}>
                                            <FaSearch />
                                        </li>
                                    </ul>
                                    {isSearchOpen && (
                                        <div className="header__search-dropdown">
                                            <input 
                                                type="text"
                                                value={searchKeyword}
                                                onChange={(e) => setSearchKeyword(e.target.value)}
                                                placeholder={t('common.search_placeholder', 'Nhập từ khóa tìm kiếm...')}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && searchKeyword.trim()) {
                                                        navigate(`/search?q=${encodeURIComponent(searchKeyword.trim())}`);
                                                        setIsSearchOpen(false);
                                                    }
                                                }}
                                            />
                                            <button onClick={() => {
                                                if (searchKeyword.trim()) {
                                                    navigate(`/search?q=${encodeURIComponent(searchKeyword.trim())}`);
                                                    setIsSearchOpen(false);
                                                }
                                            }}>
                                                <FaSearch />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Extra Levels (Level 3, Level 4, ...) - Tự động break xuống dòng tiếp theo */}
                                {!activeMenuMobile && extraLevels.map(lvl => (
                                    <div key={lvl} className={`header__menu-sub2-row header__menu-sub-extra-row header__menu-level-${lvl}`}>
                                        <ul className="header__menu-list header__menu-text header__menu-text-sub2">
                                            {menusByLevel[lvl].map((value, index) => renderMenuItem(value, index))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Header
